import EditFieldModal from "@/modules/common/components/modals/edit-field-modal";
import {
  Box,
  Button,
  Stack,
  Step,
  StepConnector,
  StepLabel,
  Stepper,
} from "@mui/material";
import useFinancialProposalMultiStepForm, {
  FinancialDetailsFormFields,
  ProposalDetailsFormFields,
  UnitDetailsFormFields,
} from "@/modules/property-tracks/hooks/rental-financial-proposal/multi-step-form.hook";
import { PreviewProposalDto } from "@/modules/property-tracks/dtos/rental-financial-proposal";
import ModalFooter from "@/modules/common/components/modals/modal-footer";
import ModalFooterUIKit from "@/modules/common/components/ui-kit/modals/modal-footer";
import { useEffect, useRef } from "react";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import CommonBottomSheet from "@/modules/common/components/ui-kit/bottom-sheets/common-bottom-sheet";
import ModalHeader from "@/modules/common/components/ui-kit/modals/modal-header";

interface RentalFinancialProposalFormProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  defaultValues: {
    unitDetails?: Partial<UnitDetailsFormFields>;
    financialDetails?: Partial<FinancialDetailsFormFields>;
    proposalDetails?: Partial<ProposalDetailsFormFields>;
  };
  onSubmit: (body: PreviewProposalDto) => void;
  isSubmitting: boolean;
}

const FormContent = ({
  activeStep,
  steps,
  currentStep,
}: {
  activeStep: number;
  steps: {
    value: number;
    title: string;
    content: React.ReactNode;
  }[];
  currentStep: {
    value: number;
    title: string;
    content: React.ReactNode;
  };
}) => {
  return (
    <>
      <Box
        sx={{
          minHeight: "fit-content",
          width: "100%",
          overflow: "auto",
          scrollbarWidth: "none",
        }}
      >
        <Stepper
          activeStep={activeStep}
          connector={<StepConnector sx={{ minWidth: "10px" }} />}
        >
          {steps.map((step) => (
            <Step key={step.value}>
              <StepLabel>{step.title}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
      {currentStep.content}
    </>
  );
};

export default function RentalFinancialProposalForm({
  open,
  setOpen,
  defaultValues,
  onSubmit,
  isSubmitting,
}: RentalFinancialProposalFormProps) {
  const { isMobile } = useScreenSize();
  const modalContentRef = useRef<HTMLDivElement>(null);

  const {
    steps,
    currentStep,
    activeStep,
    clearForms,
    previousStep,
    isLastStep,
    triggerPreview,
    previewLoading,
    PreviewModal,
    reset,
  } = useFinancialProposalMultiStepForm({
    handleSubmit: onSubmit,
    defaultValues,
  });

  const handleClose = () => {
    setOpen(false);
    clearForms();
  };

  useEffect(() => {
    if (!open) clearForms();
    else reset(defaultValues);
  }, [open]);

  useEffect(() => {
    modalContentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeStep]);

  return (
    open && (
      <>
        {isMobile ? (
          <CommonBottomSheet
            isOpen={open}
            onClose={handleClose}
            snapPoints={[0.8, 0.8]}
          >
            <ModalHeader
              title="Generate financial proposal"
              onClose={handleClose}
            />
            <Stack gap={2} overflow="auto" width="100%" height="80%" p={2}>
              <FormContent
                activeStep={activeStep}
                steps={steps}
                currentStep={currentStep}
              />
            </Stack>
            <Box position="sticky" bottom={0}>
              <ModalFooterUIKit
                confirmButtonText={isLastStep ? "Generate Proposal" : "Next"}
                onExtra={previousStep}
                extraButtonText={activeStep > 0 ? "Back" : ""}
                cancelButtonText={isLastStep ? "Preview" : ""}
                isLoading={isSubmitting || previewLoading}
                isConfirmDisabled={isLastStep || isSubmitting || previewLoading}
                onConfirm={currentStep.onNext}
                onCancel={() => triggerPreview()}
              />
            </Box>
          </CommonBottomSheet>
        ) : (
          <EditFieldModal
            ref={modalContentRef}
            open={open}
            setOpen={setOpen}
            handleCloseModal={handleClose}
            width={480}
            header={"Generate financial proposal"}
            modalFooter={false}
          >
            <FormContent
              activeStep={activeStep}
              steps={steps}
              currentStep={currentStep}
            />
            <ModalFooter
              handleClose={handleClose}
              applyButton={
                <Box sx={{ display: "flex", gap: 1 }}>
                  {activeStep > 0 && (
                    <Button
                      sx={(t) => ({
                        textTransform: "none",
                        color: t.nawy.colors.semantic.text.accent,
                        borderColor: t.nawy.colors.semantic.layer.accent,
                      })}
                      variant="outlined"
                      onClick={previousStep}
                    >
                      Back
                    </Button>
                  )}

                  <Button
                    sx={(t) => ({
                      textTransform: "none",
                      color: t.nawy.colors.semantic.text.onColor,
                      backgroundColor: t.nawy.colors.semantic.layer.accent,
                    })}
                    onClick={currentStep.onNext}
                    variant="contained"
                    loading={isSubmitting && isLastStep}
                  >
                    {isLastStep ? "Generate Proposal" : "Next"}
                  </Button>
                </Box>
              }
              cancelButton={
                isLastStep ? (
                  <Button
                    sx={(t) => ({
                      textTransform: "none",
                      color: t.nawy.colors.semantic.text.accent,
                    })}
                    onClick={() => triggerPreview()}
                    loading={previewLoading}
                  >
                    Preview
                  </Button>
                ) : (
                  <Box />
                )
              }
            />
          </EditFieldModal>
        )}
        <PreviewModal />
      </>
    )
  );
}
